// import api's and mutations
import { mutations } from 'RestAPIs/rapini';

// import atoms
import { planUnitsAtom } from '../../atoms';

// import components
import { LoadingButton } from '@mui/lab';
import { Box, Tooltip } from '@mui/material';
import {
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledPlanUnitSelector,
  showSnackbar
} from 'components/shared';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import ControlledNumericField from 'components/shared/ControlledFields/ControlledNumericField';

// import enums
import { ViewContext } from '../../enums';

// import hooks
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useRecoilValue } from 'recoil';
import useSelectedDate from '../../hooks/useSelectedDate';

// import types
import { InferType } from 'yup';

// import utils
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs, { Dayjs } from 'dayjs';
import { getWeekInterval } from 'utils/date';
import { getViewContext } from '../../utils';
import copyPeriodSchema from './copy-period-schema';
import {
  GridContainer,
  GridCompact10,
  GridCompact6
} from 'components/shared/Grid/Grid.components';
import { Icon } from '@sdb/planning-ui';

const schema = copyPeriodSchema();
type Form = InferType<typeof schema>;

type Props = {
  employeeEntityId?: string;
  handleClose: () => void;
};

export default function CopyPeriodForm({
  handleClose,
  employeeEntityId
}: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [defaultPlanUnit] = useRecoilValue(planUnitsAtom);
  const [selectedDate] = useSelectedDate();
  const { startDate: sourceStartDate, endDate: sourceEndDate } =
    getWeekInterval(selectedDate);
  const isMyActivityPlanning = getViewContext() === ViewContext.Own;

  const defaultValues = {
    hasProject: !!employeeEntityId,
    copyPeriodInterval: 1,
    includeEmployee: false,
    organizationalUnit: !employeeEntityId ? defaultPlanUnit : undefined,
    sourceStartDate: dayjs(sourceStartDate),
    sourceEndDate: dayjs(sourceEndDate),
    startDate: dayjs(sourceEndDate).add(1, 'day'),
    endDate: dayjs(sourceEndDate).add(7, 'day')
  };

  const methods = useForm<Form>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      ...defaultValues,
      organizationalUnit: !employeeEntityId ? defaultPlanUnit : undefined
    }
  });

  const { control, handleSubmit, reset, watch } = methods;
  const copyWeekInterval = watch('copyWeekInterval');

  const { mutate: copyAppointmentsInPeriod, isLoading } =
    mutations.useCopyAppointmentsInPeriod();

  const defaultSourceEndDate = methods.formState.defaultValues
    ?.sourceEndDate as Dayjs;
  const shouldShowCopyWeekIntervalField =
    dayjs(defaultSourceEndDate).isValid() &&
    dayjs(defaultSourceEndDate).format('YYYY-MM-DD') ===
      dayjs(methods.watch('sourceEndDate')).format('YYYY-MM-DD') &&
    methods.formState.dirtyFields.endDate;
  function onSubmit(data: Form) {
    const {
      organizationalUnit,
      startDate,
      endDate,
      includeEmployee,
      copyWeekInterval
    } = data;

    const payload = {
      organizationalUnitId: organizationalUnit?.id,
      employeeEntityId,
      includeEmployees: includeEmployee ?? false,
      weekInterval: copyWeekInterval,
      source: {
        startDate: dayjs(sourceStartDate).format('YYYY-MM-DD'),
        endDate: dayjs(sourceEndDate).format('YYYY-MM-DD')
      },
      destination: {
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD')
      }
    };

    copyAppointmentsInPeriod(payload, {
      onSuccess: (response: any) => {
        if (response.notifications?.length > 0) {
          showSnackbar({
            severity: 'warning',
            message: t('activityPlanning.notAllAppointmentsCopied')
          });
        } else {
          showSnackbar({ severity: 'success', message: t('changesSaved') });
        }
        queryClient.invalidateQueries('planning');
        reset(defaultValues);
        handleClose();
      }
    });

    return;
  }

  function dateIsAMonday(date: dayjs.Dayjs) {
    return date.isoWeekday() === 1;
  }

  function dateIsASunday(date: dayjs.Dayjs) {
    return date.isoWeekday() === 7;
  }

  const conformSourceAndDestinationDates = () => {
    const { endDate, sourceStartDate, sourceEndDate, startDate } =
      methods.watch();
    const sourceDifferenceInDays =
      sourceEndDate?.diff(sourceStartDate, 'days') || 0;

    if (Math.abs(startDate?.diff(endDate, 'days')) < sourceDifferenceInDays) {
      methods.setValue(
        'endDate',
        startDate.add(sourceDifferenceInDays, 'days'),
        { shouldValidate: true }
      );
    }

    if (Math.abs(sourceStartDate!.diff(sourceEndDate, 'days')) < 7) {
      methods.setValue('sourceEndDate', sourceStartDate!.add(6, 'days'), {
        shouldValidate: true
      });
    }

    methods.trigger([
      'startDate',
      'endDate',
      'sourceStartDate',
      'sourceEndDate'
    ]);
  };

  return (
    <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
      {!employeeEntityId && (
        <MultiStepForm.Section
          title={t('where')}
          validateFields={['organizationalUnit']}
        >
          <ControlledPlanUnitSelector
            name="organizationalUnit"
            control={control}
            hideGroups
            hideNavigation
            useEmployeePlanunits
            isSingleSelect
            variant="outlined"
          />
        </MultiStepForm.Section>
      )}
      <MultiStepForm.Section title={t('source')}>
        <GridContainer>
          <GridCompact6>
            <ControlledDatePicker
              name="sourceStartDate"
              label="Startdatum"
              control={control}
              variant="outlined"
              disabled
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledDatePicker
              name="sourceEndDate"
              label="Einddatum"
              control={control}
              variant="outlined"
              shouldDisableDate={(date) => !dateIsASunday(date)}
              onChange={conformSourceAndDestinationDates.bind(undefined)}
            />
          </GridCompact6>
        </GridContainer>
      </MultiStepForm.Section>
      <MultiStepForm.Section
        title="Bestemming"
        validateFields={['startDate', 'endDate']}
      >
        <GridContainer>
          <GridCompact6>
            <ControlledDatePicker
              name="startDate"
              label="Startdatum"
              control={control}
              variant="outlined"
              shouldDisableDate={(date) => !dateIsAMonday(date)}
              onChange={conformSourceAndDestinationDates.bind(undefined)}
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledDatePicker
              name="endDate"
              label="Einddatum"
              control={control}
              variant="outlined"
              shouldDisableDate={(date) => !dateIsASunday(date)}
              onChange={conformSourceAndDestinationDates.bind(undefined)}
            />
          </GridCompact6>
        </GridContainer>
        <GridContainer>
          {shouldShowCopyWeekIntervalField && (
            <GridCompact10 display="flex" alignItems="center">
              <ControlledNumericField
                name="copyWeekInterval"
                label={t('activityPlanning.copyWeekInterval')}
                step={1}
                minValue={1}
                sx={{ mr: 1 }}
              />
              <Tooltip
                title={t(
                  `activityPlanning.${
                    !copyWeekInterval || copyWeekInterval === 1
                      ? 'copyEveryWeekInfo'
                      : 'copyWeekIntervalInfo'
                  }`,
                  {
                    weeks: copyWeekInterval || 1
                  }
                )}
                arrow
                placement="bottom"
              >
                <Icon type="circle-info" fontSize="small" />
              </Tooltip>
            </GridCompact10>
          )}
        </GridContainer>
      </MultiStepForm.Section>
      {!isMyActivityPlanning && (
        <MultiStepForm.Section title={t('settings')}>
          <ControlledCheckbox
            control={control}
            name="includeEmployee"
            label={t('activityPlanning.includeEmployeeInCopy')}
          />
        </MultiStepForm.Section>
      )}
      <MultiStepForm.Actions>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
          gap={2}
        >
          <Box display="flex" ml="auto">
            <LoadingButton
              loading={isLoading}
              type="submit"
              variant="contained"
              autoFocus
              color="primary"
            >
              {t('copy')}
            </LoadingButton>
          </Box>
        </Box>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
}
